import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthenticatedHttpService } from '../authenticatedHttpService.service';
import { LogoutInfo } from '../auth.service';
import * as Sentry from '@sentry/angular';

@Injectable({ providedIn: 'root' })
export class SentryLogProvider {
  constructor(private http: AuthenticatedHttpService) {}

  init() {
    Sentry.init({
      dsn: environment.sentry.dsn,
    });
  }

  setUser(user: Sentry.User) {
    Sentry.setUser(user);
  }

  setDevice(device: any) {
    Sentry.setContext('device', device);
  }

  setEnvironment(env: any) {
    Sentry.setContext('environment', env);
  }

  reportSessionDrop(info: LogoutInfo): void {
    const userId = info.userId || this.http.userId || undefined;
    const email = info.email || this.http.getUserName() || undefined;
    const databaseIdentifier =
      info.databaseIdentifier || this.http.getUserDatabaseIdentifier() || undefined;

    Sentry.withScope((scope) => {
      scope.setLevel('info');
      scope.setTag('topic', 'session-drop');
      scope.setTag('logout_reason', info.reason);

      if (userId || email) {
        scope.setUser({ id: userId, email });
      }

      scope.setExtra('sessionDrop', {
        reason: info.reason,
        userId: userId ?? null,
        email: email ?? null,
        databaseIdentifier: databaseIdentifier ?? null,
        occurredAt: new Date().toISOString(),
      });

      Sentry.captureMessage(`Session dropped: ${info.reason}`);
    });
  }

  trace(step: string, data?: any): void {
    try {
      console.log(`[trace] ${step}`, data ?? '');
      Sentry.addBreadcrumb({
        category: 'flow',
        level: 'info',
        message: step,
        data: this.safeData(data),
      });
    } catch {
      // tracing must never break the flow
    }
  }

  reportFlowEvent(topic: string, step: string, data?: any): void {
    try {
      console.log(`[flow-event] ${topic} / ${step}`, data ?? '');
      Sentry.withScope((scope) => {
        scope.setLevel('info');
        scope.setTag('topic', topic);
        scope.setTag('flow_step', step);
        scope.setExtra('flowData', this.safeData(data));
        Sentry.captureMessage(`${topic}: ${step}`);
      });
    } catch {
      // ignore
    }
  }

  reportFlowIssue(topic: string, step: string, data?: any): void {
    try {
      console.warn(`[flow-issue] ${topic} / ${step}`, data ?? '');
      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setTag('topic', topic);
        scope.setTag('flow_step', step);
        scope.setExtra('flowData', this.safeData(data));
        Sentry.captureMessage(`${topic}: ${step}`);
      });
    } catch {
      // ignore
    }
  }

  reportFlowError(topic: string, step: string, error: any, data?: any): void {
    try {
      console.error(`[flow-error] ${topic} / ${step}`, error, data ?? '');
      Sentry.withScope((scope) => {
        scope.setLevel('error');
        scope.setTag('topic', topic);
        scope.setTag('flow_step', step);
        scope.setExtra('flowData', this.safeData(data));
        scope.setExtra('errorMessage', error?.message ?? String(error));
        scope.setExtra('errorStack', error?.stack ?? null);
        Sentry.captureException(error instanceof Error ? error : new Error(`${step}: ${error}`));
      });
    } catch {
      // ignore
    }
  }

  private safeData(data: any): any {
    if (data == null) return null;
    try {
      return JSON.parse(JSON.stringify(data));
    } catch {
      return { unserializable: String(data) };
    }
  }

  logWithDebugLine(
    message: string,
    debugLine: number,
    withScreenshot?: boolean,
    topic?: string
  ): void {
    this.log(`(${debugLine}): ` + message, withScreenshot, {}, topic);
  }

  logWithDebugLineAndObject(
    message: string,
    debugLine: number,
    withScreenshot?: boolean,
    extraData?: object,
    topic?: string
  ): void {
    this.log(`(${debugLine}): ` + message, withScreenshot, extraData, topic);
  }

  log(message: string, withScreenshot?: boolean, extraData?: object, topic?: string): void {
    if (this.http.getUserName() === 'admin@rota.at') return;

    if (withScreenshot) {
      const root = document.querySelector('#app') as HTMLElement;
      if (!root) return;
    } else {
      Sentry.withScope((scope) => {
        scope.setLevel('log');

        if (topic) {
          scope.setTag('topic', topic);
        }

        if (extraData) {
          try {
            const serializedData = JSON.stringify(extraData);
            scope.setExtra('extraData', serializedData);
          } catch (error) {
            console.error('Failed to serialize extraData:', error);
          }
        }

        Sentry.captureMessage(message);
      });
    }
  }
}
